import { useState, useEffect, useRef, useCallback } from "react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Checkbox } from "../components/ui/checkbox";
import { toast } from "sonner";
import { Plus, Bell, CalendarClock, FileText, History, Pencil, Trash2, Send, Eye, RotateCcw, AlertTriangle, CheckCircle2, RefreshCw, Mail, MessageCircle, Filter } from "lucide-react";

const VARIABLES = [
  { tag: "{{destinataire_nom}}", label: "Nom destinataire" },
  { tag: "{{vehicule_immatriculation}}", label: "Immatriculation" },
  { tag: "{{vehicule_marque}}", label: "Marque" },
  { tag: "{{vehicule_modele}}", label: "Modele" },
  { tag: "{{date_echeance}}", label: "Date echeance" },
  { tag: "{{gestionnaire_nom}}", label: "Nom gestionnaire" },
];

const frequenceLabels = { unique: "Unique", recurrent: "Recurrent" };
const typeLabels = {
  assurance: { label: "Assurance", color: "bg-blue-100 text-blue-800 border-blue-300" },
  visite_technique: { label: "Visite technique", color: "bg-amber-100 text-amber-800 border-amber-300" },
  entretien: { label: "Entretien", color: "bg-teal-100 text-teal-800 border-teal-300" },
  vidange: { label: "Vidange", color: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  credit_gps: { label: "Credit GPS", color: "bg-orange-100 text-orange-800 border-orange-300" },
  custom: { label: "Personnalise", color: "bg-violet-100 text-violet-800 border-violet-300" },
};

function HtmlEditor({ value, onChange }) {
  const editorRef = useRef(null);
  const isInternalChange = useRef(false);

  useEffect(() => {
    if (editorRef.current && !isInternalChange.current) {
      if (editorRef.current.innerHTML !== value) {
        editorRef.current.innerHTML = value || "";
      }
    }
    isInternalChange.current = false;
  }, [value]);

  const handleInput = useCallback(() => {
    isInternalChange.current = true;
    onChange(editorRef.current.innerHTML);
  }, [onChange]);

  const exec = (cmd, val = null) => { document.execCommand(cmd, false, val); editorRef.current?.focus(); };

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden" data-testid="html-editor">
      <div className="flex items-center gap-0.5 p-1.5 bg-slate-50 border-b border-slate-200 flex-wrap">
        <button type="button" onClick={() => exec("bold")} className="px-2 py-1 rounded text-xs font-bold hover:bg-slate-200 transition-colors" title="Gras">G</button>
        <button type="button" onClick={() => exec("italic")} className="px-2 py-1 rounded text-xs italic hover:bg-slate-200 transition-colors" title="Italique">I</button>
        <button type="button" onClick={() => exec("underline")} className="px-2 py-1 rounded text-xs underline hover:bg-slate-200 transition-colors" title="Souligne">S</button>
        <div className="w-px h-5 bg-slate-300 mx-1"></div>
        <button type="button" onClick={() => exec("insertUnorderedList")} className="px-2 py-1 rounded text-xs hover:bg-slate-200 transition-colors" title="Liste">Liste</button>
        <button type="button" onClick={() => exec("insertOrderedList")} className="px-2 py-1 rounded text-xs hover:bg-slate-200 transition-colors" title="Liste numerotee">1.2.3</button>
        <div className="w-px h-5 bg-slate-300 mx-1"></div>
        <button type="button" onClick={() => exec("formatBlock", "h3")} className="px-2 py-1 rounded text-xs font-semibold hover:bg-slate-200 transition-colors" title="Titre">H3</button>
        <button type="button" onClick={() => exec("formatBlock", "p")} className="px-2 py-1 rounded text-xs hover:bg-slate-200 transition-colors" title="Paragraphe">P</button>
        <div className="w-px h-5 bg-slate-300 mx-1"></div>
        <select onChange={e => { if (e.target.value) { exec("insertText", e.target.value); e.target.value = ""; } }} className="text-xs border border-slate-200 rounded px-1 py-0.5 bg-white" data-testid="insert-variable">
          <option value="">Inserer variable...</option>
          {VARIABLES.map(v => <option key={v.tag} value={v.tag}>{v.label}</option>)}
        </select>
      </div>
      <div ref={editorRef} contentEditable onInput={handleInput} className="min-h-[200px] p-3 text-sm focus:outline-none prose prose-sm max-w-none" data-testid="html-editor-content" />
    </div>
  );
}

function getDaysUntil(dateStr) {
  if (!dateStr) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr + "T00:00:00"); target.setHours(0, 0, 0, 0);
  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [history, setHistory] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Dialogs
  const [showSchedule, setShowSchedule] = useState(false);
  const [showTemplate, setShowTemplate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSend, setShowSend] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  // Editing
  const [editSchedule, setEditSchedule] = useState(null);
  const [editTemplate, setEditTemplate] = useState(null);
  const [editAlert, setEditAlert] = useState(null);
  const [previewHtml, setPreviewHtml] = useState("");

  // Filters (Alertes tab)
  const defaultTypes = Object.keys(typeLabels).filter(k => k !== "custom");
  const [filterTypes, setFilterTypes] = useState(defaultTypes);
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterVehicule, setFilterVehicule] = useState("all");
  const toggleFilterType = (type) => {
    setFilterTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
  };

  // Forms
  const [scheduleForm, setScheduleForm] = useState({ nom: "", type: "custom", description: "", jours_avant: 30, frequence: "unique", destinataires: ["gestionnaire"] });
  const [templateForm, setTemplateForm] = useState({ nom: "", sujet: "", contenu_html: "", type_alerte: "custom" });
  const [sendForm, setSendForm] = useState({ template_id: "", vehicule_id: "", destinataires: [], sujet: "", contenu: "" });
  const [alertForm, setAlertForm] = useState({ vehicule_id: "", type: "custom", titre: "", description: "", date_echeance: "" });

  const fetchData = async () => {
    try {
      const [aRes, sRes, tRes, hRes, vRes, uRes] = await Promise.all([
        api.get("/alerts"), api.get("/alert-schedules"), api.get("/alert-templates"), api.get("/alert-history"), api.get("/vehicles"), api.get("/users")
      ]);
      setAlerts(aRes.data); setSchedules(sRes.data); setTemplates(tRes.data); setHistory(hRes.data); setVehicles(vRes.data); setUsers(uRes.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const getUserName = (id) => { const u = users.find(usr => usr.id === id); return u ? `${u.prenom} ${u.nom}` : id; };
  const getVehicleName = (id) => { const v = vehicles.find(vh => vh.id === id); return v ? `${v.immatriculation} - ${v.marque} ${v.modele}` : "-"; };
  const getTypeInfo = (type) => typeLabels[type] || typeLabels.custom;

  // =========== ALERTS CRUD ===========
  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editAlert) {
        await api.put(`/alerts/${editAlert.id}`, alertForm);
        toast.success("Alerte mise a jour");
      } else {
        await api.post("/alerts", alertForm);
        toast.success("Alerte creee");
      }
      setShowAlert(false); setEditAlert(null);
      setAlertForm({ vehicule_id: "", type: "custom", titre: "", description: "", date_echeance: "" });
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  };

  const handleDeleteAlert = async (id) => {
    if (!window.confirm("Supprimer cette alerte ?")) return;
    try { await api.delete(`/alerts/${id}`); toast.success("Alerte supprimee"); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const handleToggleAlertStatut = async (alert) => {
    const newStatut = alert.statut === "active" ? "inactive" : "active";
    try { await api.put(`/alerts/${alert.id}`, { statut: newStatut }); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const openEditAlert = (a) => {
    setEditAlert(a);
    setAlertForm({ vehicule_id: a.vehicule_id, type: a.type, titre: a.titre, description: a.description || "", date_echeance: a.date_echeance });
    setShowAlert(true);
  };

  const resetAlertFilters = () => { setFilterTypes(defaultTypes); setFilterStatut("all"); setFilterVehicule("all"); };

  const filteredAlerts = alerts
    .filter(a => filterTypes.length === 0 || filterTypes.includes(a.type))
    .filter(a => filterStatut === "all" || a.statut === filterStatut)
    .filter(a => filterVehicule === "all" || a.vehicule_id === filterVehicule);

  const alertsActives = alerts.filter(a => a.statut === "active").length;
  const alertesExpired = alerts.filter(a => { const d = getDaysUntil(a.date_echeance); return d !== null && d < 0; }).length;
  const alertesSoon = alerts.filter(a => { const d = getDaysUntil(a.date_echeance); return d !== null && d >= 0 && d <= 7; }).length;

  // =========== SCHEDULE CRUD ===========
  const handleScheduleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editSchedule) {
        await api.put(`/alert-schedules/${editSchedule.id}`, scheduleForm);
        toast.success("Planification mise a jour");
      } else {
        await api.post("/alert-schedules", scheduleForm);
        toast.success("Planification creee");
      }
      setShowSchedule(false); setEditSchedule(null);
      setScheduleForm({ nom: "", type: "custom", description: "", jours_avant: 30, frequence: "unique", destinataires: ["gestionnaire"] });
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDeleteSchedule = async (id) => {
    if (!window.confirm("Supprimer cette planification ?")) return;
    try { await api.delete(`/alert-schedules/${id}`); toast.success("Supprimee"); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  const handleToggleSchedule = async (schedule) => {
    try { await api.put(`/alert-schedules/${schedule.id}`, { actif: !schedule.actif }); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  // =========== TEMPLATE CRUD ===========
  const handleTemplateSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editTemplate) {
        await api.put(`/alert-templates/${editTemplate.id}`, templateForm);
        toast.success("Template mis a jour");
      } else {
        await api.post("/alert-templates", templateForm);
        toast.success("Template cree");
      }
      setShowTemplate(false); setEditTemplate(null);
      setTemplateForm({ nom: "", sujet: "", contenu_html: "", type_alerte: "custom" });
      fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  const handleDeleteTemplate = async (id) => {
    if (!window.confirm("Supprimer ce template ?")) return;
    try { await api.delete(`/alert-templates/${id}`); toast.success("Supprime"); fetchData(); } catch (e) { toast.error("Erreur"); }
  };

  // Send alert
  const openSendDialog = (template) => {
    setSendForm({ template_id: template.id, vehicule_id: "", destinataires: [], sujet: template.sujet, contenu: template.contenu_html });
    setShowSend(true);
  };

  const handleSend = async () => {
    if (sendForm.destinataires.length === 0) { toast.error("Selectionnez au moins un destinataire"); return; }
    try {
      const destNames = sendForm.destinataires.map(id => getUserName(id));
      await api.post("/alert-history", {
        template_id: sendForm.template_id, vehicule_id: sendForm.vehicule_id,
        type_alerte: templates.find(t => t.id === sendForm.template_id)?.type_alerte || "",
        sujet: sendForm.sujet, contenu: sendForm.contenu, destinataires: destNames, statut: "envoye"
      });
      toast.success(`Alerte envoyee a ${destNames.length} destinataire(s)`);
      setShowSend(false); fetchData();
    } catch (e) { toast.error("Erreur"); }
  };

  // Process notifications
  const [processing, setProcessing] = useState(false);
  const handleProcessNotifications = async () => {
    setProcessing(true);
    try {
      const res = await api.post("/alerts/process-notifications");
      const d = res.data;
      if (d.notifications_sent > 0) {
        toast.success(`${d.notifications_sent} notification(s) envoyee(s)`);
      } else {
        toast.info("Aucune nouvelle notification a envoyer");
      }
      fetchData();
    } catch (e) { toast.error("Erreur lors du traitement"); } finally { setProcessing(false); }
  };

  if (loading) return <div className="text-slate-500">Chargement...</div>;

  return (
    <div data-testid="alerts-page">
      <Tabs defaultValue="alertes">
        <div className="page-header">
          <div>
            <h1 className="page-title">Module Alertes</h1>
            <p className="text-sm text-slate-500 mt-1">{alertsActives} alerte(s) active(s), {schedules.filter(s => s.actif).length} planification(s), {templates.length} template(s)</p>
          </div>
          <TabsList>
            <TabsTrigger value="alertes" data-testid="tab-alertes"><Bell className="w-4 h-4 mr-1" />Alertes</TabsTrigger>
            <TabsTrigger value="planifications" data-testid="tab-planifications"><CalendarClock className="w-4 h-4 mr-1" />Planifications</TabsTrigger>
            <TabsTrigger value="templates" data-testid="tab-templates"><FileText className="w-4 h-4 mr-1" />Templates</TabsTrigger>
            <TabsTrigger value="historique" data-testid="tab-historique"><History className="w-4 h-4 mr-1" />Historique</TabsTrigger>
          </TabsList>
        </div>

        {/* ========== ALERTES ========== */}
        <TabsContent value="alertes">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6" data-testid="alerts-stats">
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-blue-50 flex items-center justify-center"><Bell className="w-4 h-4 text-[#0F52BA]" /></div></div>
              <p className="text-xl font-bold text-[#0F52BA] font-heading">{alertsActives}</p>
              <p className="text-xs text-slate-500">Actives</p>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-amber-600" /></div></div>
              <p className="text-xl font-bold text-amber-600 font-heading">{alertesSoon}</p>
              <p className="text-xs text-slate-500">Echeance &lt; 7j</p>
            </div>
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-2"><div className="w-8 h-8 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle className="w-4 h-4 text-red-600" /></div></div>
              <p className="text-xl font-bold text-red-600 font-heading">{alertesExpired}</p>
              <p className="text-xs text-slate-500">Expirees</p>
            </div>
          </div>

          {/* Filters + Add */}
          <div className="flex flex-wrap items-center gap-2 sm:gap-3 mb-4">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="w-full sm:w-[220px] justify-between text-sm" data-testid="filter-alert-type">
                  <span className="truncate">
                    {filterTypes.length === Object.keys(typeLabels).length ? "Tous les types" : filterTypes.length === 0 ? "Aucun type" : filterTypes.map(t => typeLabels[t]?.label || t).join(", ")}
                  </span>
                  <Filter className="w-3.5 h-3.5 ml-2 shrink-0 text-slate-400" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-[220px] p-2" align="start">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-slate-50 rounded" onClick={() => setFilterTypes(filterTypes.length === Object.keys(typeLabels).length ? [] : Object.keys(typeLabels))}>
                    <Checkbox checked={filterTypes.length === Object.keys(typeLabels).length} />
                    <span className="text-sm font-medium">Tous</span>
                  </div>
                  <div className="border-t my-1" />
                  {Object.entries(typeLabels).map(([k, v]) => (
                    <div key={k} className="flex items-center gap-2 px-2 py-1 cursor-pointer hover:bg-slate-50 rounded" onClick={() => toggleFilterType(k)}>
                      <Checkbox checked={filterTypes.includes(k)} />
                      <Badge variant="outline" className={`text-xs ${v.color}`}>{v.label}</Badge>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
            <Select value={filterStatut} onValueChange={setFilterStatut}>
              <SelectTrigger className="w-[150px]" data-testid="filter-alert-statut"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous statuts</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterVehicule} onValueChange={setFilterVehicule}>
              <SelectTrigger className="w-[220px]" data-testid="filter-alert-vehicule"><SelectValue placeholder="Tous vehicules" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les vehicules</SelectItem>
                {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={resetAlertFilters} data-testid="btn-reset-alert-filters"><RotateCcw className="w-4 h-4 mr-1" />Reinitialiser</Button>
            <div className="ml-auto flex gap-2">
              <Button variant="outline" onClick={handleProcessNotifications} disabled={processing} data-testid="btn-process-notifications">
                <RefreshCw className={`w-4 h-4 mr-2 ${processing ? "animate-spin" : ""}`} />{processing ? "Traitement..." : "Traiter les notifications"}
              </Button>
              <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setEditAlert(null); setAlertForm({ vehicule_id: "", type: "custom", titre: "", description: "", date_echeance: "" }); setShowAlert(true); }} data-testid="btn-add-alert">
                <Plus className="w-4 h-4 mr-2" />Nouvelle alerte
              </Button>
            </div>
          </div>

          {/* Alerts Table */}
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Titre</TableHead>
                  <TableHead className="font-semibold text-slate-600">Type</TableHead>
                  <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                  <TableHead className="font-semibold text-slate-600">Echeance</TableHead>
                  <TableHead className="font-semibold text-slate-600">Jours restants</TableHead>
                  <TableHead className="font-semibold text-slate-600">Source</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAlerts.map(a => {
                  const ti = getTypeInfo(a.type);
                  const days = getDaysUntil(a.date_echeance);
                  const dayColor = days === null ? "text-slate-400" : days < 0 ? "text-red-600 font-bold" : days <= 7 ? "text-red-600 font-semibold" : days <= 30 ? "text-amber-600 font-medium" : "text-emerald-600";
                  const dayText = days === null ? "-" : days < 0 ? `${Math.abs(days)}j en retard` : days === 0 ? "Aujourd'hui" : `${days}j`;
                  return (
                    <TableRow key={a.id} data-testid={`alert-row-${a.id}`} className={a.statut !== "active" ? "opacity-50" : ""}>
                      <TableCell className="font-medium text-slate-900 max-w-[220px]">{a.titre}</TableCell>
                      <TableCell><Badge variant="outline" className={ti.color}>{ti.label}</Badge></TableCell>
                      <TableCell className="text-slate-600 text-sm">
                        {a.vehicule_nom ? <>{a.vehicule_nom}<br /><span className="text-xs text-slate-400">{a.vehicule_immat}</span></> : "-"}
                      </TableCell>
                      <TableCell className="text-slate-600">{a.date_echeance ? new Date(a.date_echeance + "T12:00:00").toLocaleDateString("fr-FR") : "-"}</TableCell>
                      <TableCell className={dayColor}>{dayText}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className={a.auto_generated ? "bg-slate-100 text-slate-600 border-slate-300" : "bg-violet-50 text-violet-700 border-violet-200"}>
                          {a.auto_generated ? "Auto" : "Manuelle"}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Switch checked={a.statut === "active"} onCheckedChange={() => handleToggleAlertStatut(a)} data-testid={`toggle-alert-${a.id}`} />
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEditAlert(a)} data-testid={`btn-edit-alert-${a.id}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteAlert(a.id)} data-testid={`btn-delete-alert-${a.id}`}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredAlerts.length === 0 && (
                  <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12">
                    <Bell className="w-10 h-10 mx-auto mb-3 text-slate-300" />Aucune alerte{filterTypes.length < Object.keys(typeLabels).length || filterStatut !== "all" || filterVehicule !== "all" ? " pour ces filtres" : ""}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ========== PLANIFICATIONS ========== */}
        <TabsContent value="planifications">
          <div className="flex justify-end mb-4">
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setEditSchedule(null); setScheduleForm({ nom: "", type: "custom", description: "", jours_avant: 30, frequence: "unique", destinataires: ["gestionnaire"] }); setShowSchedule(true); }} data-testid="btn-add-schedule">
              <Plus className="w-4 h-4 mr-2" />Planification
            </Button>
          </div>
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Nom</TableHead>
                  <TableHead className="font-semibold text-slate-600">Type</TableHead>
                  <TableHead className="font-semibold text-slate-600">Description</TableHead>
                  <TableHead className="font-semibold text-slate-600">Delai</TableHead>
                  <TableHead className="font-semibold text-slate-600">Frequence</TableHead>
                  <TableHead className="font-semibold text-slate-600">Destinataires</TableHead>
                  <TableHead className="font-semibold text-slate-600">Actif</TableHead>
                  <TableHead className="font-semibold text-slate-600 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {schedules.map(s => {
                  const ti = getTypeInfo(s.type);
                  return (
                    <TableRow key={s.id} data-testid={`schedule-row-${s.id}`} className={!s.actif ? "opacity-50" : ""}>
                      <TableCell className="font-medium text-slate-900">{s.nom}</TableCell>
                      <TableCell><Badge variant="outline" className={ti.color}>{ti.label}</Badge></TableCell>
                      <TableCell className="text-slate-600 text-sm max-w-[200px] truncate">{s.description}</TableCell>
                      <TableCell className="text-slate-700 font-medium">{s.jours_avant}j avant</TableCell>
                      <TableCell><Badge variant="outline">{frequenceLabels[s.frequence] || s.frequence}</Badge></TableCell>
                      <TableCell className="text-sm text-slate-600">{(s.destinataires || []).join(", ")}</TableCell>
                      <TableCell><Switch checked={s.actif} onCheckedChange={() => handleToggleSchedule(s)} data-testid={`toggle-schedule-${s.id}`} /></TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => { setEditSchedule(s); setScheduleForm({ nom: s.nom, type: s.type, description: s.description || "", jours_avant: s.jours_avant, frequence: s.frequence, destinataires: s.destinataires || ["gestionnaire"] }); setShowSchedule(true); }} data-testid={`btn-edit-schedule-${s.id}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => handleDeleteSchedule(s.id)} data-testid={`btn-delete-schedule-${s.id}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {schedules.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-8"><CalendarClock className="w-8 h-8 mx-auto mb-2 text-slate-300" />Aucune planification</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* ========== TEMPLATES ========== */}
        <TabsContent value="templates">
          <div className="flex justify-end mb-4">
            <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={() => { setEditTemplate(null); setTemplateForm({ nom: "", sujet: "", contenu_html: "", type_alerte: "custom" }); setShowTemplate(true); }} data-testid="btn-add-template">
              <Plus className="w-4 h-4 mr-2" />Template
            </Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map(t => {
              const ti = getTypeInfo(t.type_alerte);
              return (
                <Card key={t.id} className="group hover:shadow-md transition-shadow" data-testid={`template-card-${t.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="font-heading font-semibold text-slate-900">{t.nom}</h3>
                        <Badge variant="outline" className={`mt-1 ${ti.color}`}>{ti.label}</Badge>
                      </div>
                      <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setPreviewHtml(t.contenu_html); setShowPreview(true); }} data-testid={`btn-preview-${t.id}`}><Eye className="w-3.5 h-3.5 text-slate-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openSendDialog(t)} data-testid={`btn-send-${t.id}`}><Send className="w-3.5 h-3.5 text-[#0F52BA]" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditTemplate(t); setTemplateForm({ nom: t.nom, sujet: t.sujet, contenu_html: t.contenu_html, type_alerte: t.type_alerte }); setShowTemplate(true); }} data-testid={`btn-edit-template-${t.id}`}><Pencil className="w-3.5 h-3.5 text-slate-500" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => handleDeleteTemplate(t.id)} data-testid={`btn-delete-template-${t.id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" /></Button>
                      </div>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Sujet: {t.sujet}</p>
                    <div className="text-xs text-slate-400 bg-slate-50 rounded p-2 max-h-20 overflow-hidden" dangerouslySetInnerHTML={{ __html: t.contenu_html }} />
                    {(t.variables || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {t.variables.map(v => <Badge key={v} variant="outline" className="text-[10px] bg-blue-50 text-blue-600 border-blue-200">{`{{${v}}}`}</Badge>)}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
            {templates.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <FileText className="w-10 h-10 mx-auto mb-3 text-slate-300" />Aucun template
              </div>
            )}
          </div>
        </TabsContent>

        {/* ========== HISTORIQUE ========== */}
        <TabsContent value="historique">
          <div className="table-container">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="font-semibold text-slate-600">Date</TableHead>
                  <TableHead className="font-semibold text-slate-600">Type</TableHead>
                  <TableHead className="font-semibold text-slate-600">Sujet</TableHead>
                  <TableHead className="font-semibold text-slate-600">Vehicule</TableHead>
                  <TableHead className="font-semibold text-slate-600">Destinataires</TableHead>
                  <TableHead className="font-semibold text-slate-600">Canaux</TableHead>
                  <TableHead className="font-semibold text-slate-600">Declencheur</TableHead>
                  <TableHead className="font-semibold text-slate-600">Statut</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {history.map(h => {
                  const ti = getTypeInfo(h.type_alerte);
                  const triggerLabel = h.trigger === "veille" ? "Veille" : h.trigger === "jour_meme" ? "Jour J" : h.trigger ? `Delai (${h.trigger.replace("delai_","").replace("j","")})` : "Manuel";
                  const triggerColor = h.trigger === "jour_meme" ? "bg-red-50 text-red-700 border-red-200" : h.trigger === "veille" ? "bg-amber-50 text-amber-700 border-amber-200" : h.trigger?.startsWith("delai_") ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200";
                  const channels = h.channels || [];
                  return (
                    <TableRow key={h.id} data-testid={`history-row-${h.id}`}>
                      <TableCell className="text-slate-600 text-sm">{new Date(h.envoye_le).toLocaleDateString("fr-FR")} {new Date(h.envoye_le).toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}</TableCell>
                      <TableCell><Badge variant="outline" className={ti.color}>{ti.label}</Badge></TableCell>
                      <TableCell className="font-medium text-slate-900 max-w-[200px] truncate">{h.sujet}</TableCell>
                      <TableCell className="text-slate-600 text-sm">{h.vehicule_id ? getVehicleName(h.vehicule_id) : "-"}</TableCell>
                      <TableCell className="text-sm">{(h.destinataires || []).map((d, i) => <Badge key={i} variant="outline" className="mr-1 mb-1 text-xs">{d}</Badge>)}</TableCell>
                      <TableCell>
                        <div className="flex flex-col gap-1">
                          {channels.filter(c => c.type === "email").map((c, i) => (
                            <Badge key={`e${i}`} variant="outline" className={`text-[10px] ${c.statut === "envoye" ? "bg-blue-50 text-blue-600 border-blue-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                              <Mail className="w-3 h-3 mr-0.5" />{c.destinataire} {c.statut !== "envoye" && "(echec)"}
                            </Badge>
                          ))}
                          {channels.filter(c => c.type === "whatsapp").map((c, i) => (
                            <Badge key={`w${i}`} variant="outline" className={`text-[10px] ${c.statut === "envoye" ? "bg-emerald-50 text-emerald-600 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                              <MessageCircle className="w-3 h-3 mr-0.5" />{c.destinataire} {c.statut !== "envoye" && "(echec)"}
                            </Badge>
                          ))}
                          {channels.length === 0 && <span className="text-slate-400 text-xs">-</span>}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant="outline" className={triggerColor}>{triggerLabel}</Badge></TableCell>
                      <TableCell><Badge variant="outline" className={h.statut === "envoye" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-700 border-red-200"}>{h.statut === "envoye" ? "Envoye" : "Echec"}</Badge></TableCell>
                    </TableRow>
                  );
                })}
                {history.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-slate-500 py-12"><History className="w-10 h-10 mx-auto mb-3 text-slate-300" />Aucun envoi effectue</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Alert Dialog */}
      <Dialog open={showAlert} onOpenChange={setShowAlert}>
        <DialogContent data-testid="alert-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editAlert ? "Modifier l'alerte" : "Nouvelle alerte"}</DialogTitle></DialogHeader>
          <form onSubmit={handleAlertSubmit} className="space-y-4">
            <div><Label>Titre</Label><Input value={alertForm.titre} onChange={e => setAlertForm({ ...alertForm, titre: e.target.value })} required data-testid="input-alert-titre" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={alertForm.type} onValueChange={v => setAlertForm({ ...alertForm, type: v })}>
                  <SelectTrigger data-testid="select-alert-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehicule</Label>
                <Select value={alertForm.vehicule_id} onValueChange={v => setAlertForm({ ...alertForm, vehicule_id: v })}>
                  <SelectTrigger data-testid="select-alert-vehicule"><SelectValue placeholder="Selectionner..." /></SelectTrigger>
                  <SelectContent>
                    {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Date d'echeance</Label><Input type="date" value={alertForm.date_echeance} onChange={e => setAlertForm({ ...alertForm, date_echeance: e.target.value })} required data-testid="input-alert-date" /></div>
            <div><Label>Description (optionnel)</Label><Textarea value={alertForm.description} onChange={e => setAlertForm({ ...alertForm, description: e.target.value })} data-testid="input-alert-desc" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowAlert(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-alert">{editAlert ? "Modifier" : "Creer"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={showSchedule} onOpenChange={setShowSchedule}>
        <DialogContent data-testid="schedule-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editSchedule ? "Modifier la planification" : "Nouvelle planification"}</DialogTitle></DialogHeader>
          <form onSubmit={handleScheduleSubmit} className="space-y-4">
            <div><Label>Nom</Label><Input value={scheduleForm.nom} onChange={e => setScheduleForm({ ...scheduleForm, nom: e.target.value })} required data-testid="input-schedule-nom" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Type</Label>
                <Select value={scheduleForm.type} onValueChange={v => setScheduleForm({ ...scheduleForm, type: v })}>
                  <SelectTrigger data-testid="select-schedule-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Frequence</Label>
                <Select value={scheduleForm.frequence} onValueChange={v => setScheduleForm({ ...scheduleForm, frequence: v })}>
                  <SelectTrigger data-testid="select-schedule-frequence"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unique">Unique</SelectItem>
                    <SelectItem value="recurrent">Recurrent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Jours avant echeance</Label><Input type="number" value={scheduleForm.jours_avant} onChange={e => setScheduleForm({ ...scheduleForm, jours_avant: parseInt(e.target.value || 0) })} data-testid="input-schedule-jours" /></div>
            <div><Label>Description</Label><Textarea value={scheduleForm.description} onChange={e => setScheduleForm({ ...scheduleForm, description: e.target.value })} data-testid="input-schedule-desc" /></div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowSchedule(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-schedule">{editSchedule ? "Modifier" : "Creer"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplate} onOpenChange={setShowTemplate}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="template-dialog">
          <DialogHeader><DialogTitle className="font-heading">{editTemplate ? "Modifier le template" : "Nouveau template"}</DialogTitle></DialogHeader>
          <form onSubmit={handleTemplateSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Nom</Label><Input value={templateForm.nom} onChange={e => setTemplateForm({ ...templateForm, nom: e.target.value })} required data-testid="input-template-nom" /></div>
              <div>
                <Label>Type d'alerte</Label>
                <Select value={templateForm.type_alerte} onValueChange={v => setTemplateForm({ ...templateForm, type_alerte: v })}>
                  <SelectTrigger data-testid="select-template-type"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.entries(typeLabels).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div><Label>Sujet</Label><Input value={templateForm.sujet} onChange={e => setTemplateForm({ ...templateForm, sujet: e.target.value })} placeholder="Ex: Alerte: Assurance {{vehicule_immatriculation}}" data-testid="input-template-sujet" /></div>
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label>Contenu du message</Label>
                <div className="flex gap-1">
                  {VARIABLES.slice(0, 3).map(v => (
                    <Badge key={v.tag} variant="outline" className="text-[10px] cursor-pointer hover:bg-blue-50 text-blue-600 border-blue-200" onClick={() => setTemplateForm({ ...templateForm, contenu_html: templateForm.contenu_html + v.tag })}>{v.tag}</Badge>
                  ))}
                </div>
              </div>
              <HtmlEditor value={templateForm.contenu_html} onChange={html => setTemplateForm({ ...templateForm, contenu_html: html })} />
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setShowTemplate(false)}>Annuler</Button>
              <Button type="submit" className="bg-[#0F52BA] hover:bg-[#0A3D8F]" data-testid="btn-submit-template">{editTemplate ? "Modifier" : "Creer"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-lg" data-testid="preview-dialog">
          <DialogHeader><DialogTitle className="font-heading">Apercu du template</DialogTitle></DialogHeader>
          <div className="bg-white border border-slate-200 rounded-lg p-4 prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: previewHtml }} />
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={showSend} onOpenChange={setShowSend}>
        <DialogContent data-testid="send-dialog">
          <DialogHeader><DialogTitle className="font-heading">Envoyer une alerte</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div><Label>Sujet</Label><Input value={sendForm.sujet} onChange={e => setSendForm({ ...sendForm, sujet: e.target.value })} data-testid="input-send-sujet" /></div>
            <div>
              <Label>Vehicule (optionnel)</Label>
              <Select value={sendForm.vehicule_id} onValueChange={v => setSendForm({ ...sendForm, vehicule_id: v })}>
                <SelectTrigger data-testid="select-send-vehicle"><SelectValue placeholder="Tous les vehicules" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les vehicules</SelectItem>
                  {vehicles.map(v => <SelectItem key={v.id} value={v.id}>{v.immatriculation} - {v.marque} {v.modele}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-2 block">Destinataires</Label>
              <div className="space-y-2 max-h-40 overflow-y-auto border rounded-lg p-2">
                {users.filter(u => u.role !== "chauffeur").map(u => (
                  <label key={u.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-slate-50 cursor-pointer">
                    <input type="checkbox" checked={sendForm.destinataires.includes(u.id)} onChange={e => { setSendForm(prev => ({ ...prev, destinataires: e.target.checked ? [...prev.destinataires, u.id] : prev.destinataires.filter(id => id !== u.id) })); }} className="rounded border-slate-300" data-testid={`check-dest-${u.id}`} />
                    <span className="text-sm">{u.prenom} {u.nom}</span>
                    <Badge variant="outline" className="text-[10px] ml-auto">{u.role}</Badge>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowSend(false)}>Annuler</Button>
              <Button className="bg-[#0F52BA] hover:bg-[#0A3D8F]" onClick={handleSend} data-testid="btn-confirm-send"><Send className="w-4 h-4 mr-2" />Envoyer</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
